<template>
  <div class="flex flex-col h-full w-full">
    <LoadingView v-if="isLoading" />
    <header class="flex items-center justify-between gap-4 flex-shrink-0 w-full">
      <h1 class="flex-0 text-3xl leading-9 font-medium font-headline antialiased  bg-gradient-to-r from-bhav-cool-gray-900 to-bhav-cool-gray-600 text-transparent bg-clip-text inline-block">
        Collections
      </h1>
    </header>
    <div class="flex flex-col mt-4 p-4 rounded-lg">
      <div class="inline-flex flex-col flex-wrap md:flex-row md:justify-start gap-6">
        <div class="mb-4 flex-initial">
          <label for="search" class="block text-sm font-medium leading-5 pb-1.5 text-blue-500">Search by Keyword</label>
          <SearchInput v-model="query.search" color="gray" placeholder="Search..." />
        </div>
        <div class="mb-4 flex-initial">
          <SelectMultipleField v-model="query.status" label="Published" :options="Statuses.statuses" all="All Statuses"/>
        </div>
        <div v-if="communities.length" class="mb-4 flex-initial">
          <SelectMultipleField v-model="query.community" label="Community" :options="communities.filter(community => community.status !== 'inactive').map(community => ({ label: community.meta?.info?.name ?? community.kova?.WebGroupName, value: community }))" empty="All Communities" all="All Communities" />
        </div>
        <div class="mb-4 flex-initial">
          <SelectMultipleField v-model="query.saleStatus" label="Sales Status" :options="FloorplanStatuses.statuses" all="All Sales Statuses" empty="All Sales Statuses"/>
        </div>
        <div v-if="hasFilters" class="mb-4 flex-initial sm:mt-5">
          <FormButton @click="clear" class="bg-bhav-blue-500 text-white">Clear Filters</FormButton>
        </div>
      </div>

      <PaginationTable
        empty-message="There are currently no collection items."
        v-if="!isLoading"
        :legacy="false"
        :columns="columns"
        view="collections-list"
        :data="filtered">
        <template #col-name="{row}">
          <router-link class="block py-4 whitespace-nowrap" :to="{ name: 'collection-show', params: { id: row.id } }">
            <div class="flex items-center">
              <div class="flex-shrink-0 rounded-full overflow-hidden bg-bhav-blue-400 h-10 w-10 flex items-center justify-center text-white">
                <img v-if="row.image" class="aspect-square object-cover" :src="row.image" alt="">
                <i v-else class="fak fa-collection text-xl"></i>
              </div>
              <div class="ml-4">
                <div class="text-sm leading-5 font-medium text-bhav-blue-500">
                  {{ row.meta?.info?.name || row.kova?.MarketingName }}
                  <span v-if="row?.status == 'new'" class="bg-bhav-cool-gray-50 text-bhav-blue-500 px-2 rounded-full inline-block text-xs leading-5 ml-1">{{ Statuses.label('new') }}</span>
                </div>
              </div>
            </div>
          </router-link>
        </template>
        <template #col-sales_status="{row}">
          <router-link class="block py-4 whitespace-nowrap" :to="{ name: 'collection-show', params: { id: row.id } }">
            <span class="px-3 h-8 items-center justify-center inline-flex text-xs leading-5 font-semibold rounded-3xl"
              :class="['Internal', 'Off Market', 'Sold Out', 'N/S'].includes(row.kova.CustomFields.TextCommWebStatus1) ? 'bg-bhav-red-100 text-bhav-red-500' : 'bg-bhav-green-100 text-bhav-green-600'">
              {{ FloorplanStatuses.label(row.kova.CustomFields.TextCommWebStatus1) }}
            </span>
          </router-link>
        </template>
        <template #col-published="{row}">
          <div class="flex items-center gap-2">
            <ToggleButton v-if="user.level >= 2" :id="`item-${row.id}-status`" @changed="save(row)" :checked="row.status == 'active'"
              on-color="green"
              :label="(row.status == 'active' && 'Published') || (row.status == 'unpublished' && 'Unpublished') || row.status == 'new' && Statuses.label('new')"/>
            <template v-else>
              <p title="Published" class="text-bhav-blue fas fa-square-check text-xl" v-if="row.status == 'active'"></p>
              <p title="Unpublished" class="text-oakwood-gray-400 fas fa-square-x text-xl" v-else></p>
            </template>
          </div>
        </template>
      </PaginationTable>
    </div>
  </div>
</template>
  
  <script>
  import axios from "axios";
  import LoadingView from '@/components/LoadingView.vue';
  import {ToggleButton, SelectMultipleField, SelectField, FormButton, SearchInput} from '@/components/form';
  import { mapState, mapWritableState, mapActions } from 'pinia';
  import { useCollectionStore } from "@/stores/collections";
  import Statuses from "@/enums/Statuses";
  import PaginationTable from "@/components/PaginationTable.vue";
  import { useAuthStore } from "@/stores/auth";
  import FloorplanStatuses from "@/enums/FloorplanStatuses";
  
  export default {
    setup() {
      return {
        Statuses,
        FloorplanStatuses
      }
    },
    data() {
      return {
        collections: [],
        communities: [],
        isLoading: true,
        columns: [
          { name: 'name', label: 'Name', default: true, sort: (item) => item.meta?.info?.name || item.kova?.MarketingName || '' },
          { name: 'sales_status', label: 'Sales Status', default: true, sort: (item) => item.kova?.CustomFields?.TextCommWebStatus1 || '' },
          { name: 'published', label: 'Published', default: true, sort: (item) => item.status || '' }
        ]
      }
    },
    methods: {
      ...mapActions(useCollectionStore, ['clear']),
      index() {
        axios.get(`collections`)
          .then(res => {
            this.collections = res.data;
            this.isLoading = false;
          })
          .catch(e => {
            throw e;
          });
      },
      indexCommunities() {
        axios
        .get(`divisions/communities`)
        .then((res) => {
          this.communities = res.data
            .sort((a,b) => (a.meta?.info?.name || a.kova?.WebGroupName) > (b.meta?.info?.name || b.kova?.WebGroupName) ? 1 : -1);
          this.isLoading = false;
        })
        .catch((e) => {
          throw e;
        });
      },
      save(item) {
        item.status = item.status != 'active' ? 'active' : 'inactive';
        axios.patch(`collections/${item.id}`, item)
        .then(res => {
          if(res.data?.id) {
            this.index();
          }
        })
        .catch(e => {
          throw e;
        });
      }
    },
    computed: {
      ...mapState(useAuthStore, ['user']),
      ...mapState(useCollectionStore, ['hasFilters']),
      ...mapWritableState(useCollectionStore, ['query', 'page']),
      filtered() {
        let results = this.collections;
        if (!this.query.status.includes(null)) {
          results = results.filter(item => this.query.status.includes(item.status));
        }

        if (this.query.community.length > 0) {
          let collections = this.query.community.flatMap(community => community.collections.map(collection => collection.division_id));
          results = results.filter(item => collections.includes(item.division_id))
        }

        if (this.query.saleStatus.length > 0) {
          results = results.filter(item => this.query.saleStatus.includes(item.kova.CustomFields.TextCommWebStatus1));
        }

        if (this.query.search != '') {
          const searchTerm = this.query.search.toLowerCase();
          results = results.filter(item => {
            const name = (item.meta?.info?.name || item.kova?.MarketingName || '').toLowerCase();

            return name.includes(searchTerm);
          });
        } 
        return results;
      }
    },
    watch: {
      query: {
        handler() {
          this.page = 1;
        },
        deep: true
      }
    },
    created() {
      this.index();
      this.indexCommunities();
    },
    components: { LoadingView, PaginationTable, ToggleButton, SelectMultipleField, SelectField, FormButton, SearchInput },
  }
  </script>